/* global moment, localStorage */
import { dateDisplayPatterns, formatCalendarDate } from './dateInput'
export { resolveDateDisplayLocale } from './dateInput'

const DEFAULT_DATE_FORMAT = 'EU'

let getConfig = () => ({ dateFormat: DEFAULT_DATE_FORMAT })
let getLocale = () => {
    try {
        return localStorage.language || 'es'
    } catch (_) {
        return 'es'
    }
}
let getLocationNow = () => moment.utc()

/**
 * @param {{ configGetter?: Function, localeGetter?: Function, locationNowGetter?: Function }} options
 */
export function configureDateFormatters({ configGetter, localeGetter, locationNowGetter }) {
    if (configGetter) {
        getConfig = configGetter
    }
    if (localeGetter) {
        getLocale = localeGetter
    }
    if (locationNowGetter) {
        getLocationNow = locationNowGetter
    }
}

/**
 * @param {string} [dateFormat]
 * @returns {'US'|'EU'}
 */
export function resolveDateFormatRegion(dateFormat) {
    const fmt = String(dateFormat || DEFAULT_DATE_FORMAT).trim().toUpperCase()

    if (fmt === 'US' || fmt.includes('MM/DD')) {
        return 'US'
    }

    return 'EU'
}

/**
 * @param {string} [dateFormat]
 * @returns {boolean}
 */
export function isUsDateFormat(dateFormat) {
    return resolveDateFormatRegion(dateFormat) === 'US'
}

/**
 * @param {string} [dateFormat]
 * @returns {string}
 */
export function resolveBaseDateFormat(dateFormat) {
    return isUsDateFormat(dateFormat) ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
}

/**
 * @param {import('moment').Moment} parsed
 * @param {string} locale
 * @returns {string}
 */
export function formatWeekdayLabel(parsed, locale, width = 'short') {
    const date = new Date(Date.UTC(2026, 3, 12 + parsed.day()))
    return new Intl.DateTimeFormat(String(locale || 'es').replace(/_/g, '-'), { weekday: width, timeZone: 'UTC' }).format(date).replace(/\.$/, '')
}

export function formatDisplayWeekdayFromStore(value, width = 'short') {
    const parsed = parseDisplayDateValue(value)
    return parsed ? formatWeekdayLabel(parsed, getLocale(), width) : ''
}

/**
 * Normaliza meridiano US: espacio + AM/PM en mayúsculas.
 * @param {string} formattedTime
 * @returns {string}
 */
export function normalizeUsTimeMeridiem(formattedTime) {
    return String(formattedTime).replace(/\s*(am|pm)$/i, (_, meridiem) => ` ${meridiem.toUpperCase()}`)
}

/**
 * @param {string} formattedDateTime
 * @returns {string}
 */
export function normalizeUsDateTimeMeridiem(formattedDateTime) {
    return String(formattedDateTime).replace(/(\d{1,2}:\d{2})\s*(am|pm)$/i, (_, time, meridiem) => `${time} ${meridiem.toUpperCase()}`)
}

/**
 * Conserva la firma legacy; las horas civiles ya no llevan sufijo "h".
 * @param {string} formatted
 * @param {string} dateFormat
 * @param {boolean} [hourSuffix]
 * @returns {string}
 */
export function applyHourSuffix(formatted, dateFormat, hourSuffix = false) {
    return formatted
}

/**
 * @param {string} [dateFormat]
 * @param {{ includeTime?: boolean, includeWeekday?: boolean }} [options]
 * @returns {string}
 */
export function buildDisplayPattern(dateFormat, { includeTime = false, includeWeekday = false } = {}) {
    const baseDatePattern = resolveBaseDateFormat(dateFormat)

    let pattern = baseDatePattern

    if (includeTime) {
        pattern = isUsDateFormat(dateFormat) ? `${pattern} hh:mm A` : `${pattern} HH:mm`
    }

    return pattern
}

/**
 * @param {string} [dateFormat]
 * @param {{ includeTime?: boolean, includeWeekday?: boolean }} [options]
 * @returns {string}
 */
export function buildDisplayPatternForRelative(dateFormat, options = {}) {
    const pattern = buildDisplayPattern(dateFormat, options)

    if (!options.includeWeekday) {
        return pattern
    }

    return `ddd ${pattern}`
}

/**
 * @param {*} value
 * @returns {import('moment').Moment|null}
 */
export function parseDisplayDateValue(value) {
    if (value == null || value === '') {
        return null
    }

    if (moment.isMoment(value)) {
        return value.isValid() ? value : null
    }

    if (typeof value === 'number') {
        const ms = value < 1e12 ? value * 1000 : value
        const parsed = moment(ms)
        return parsed.isValid() ? parsed : null
    }

    if (typeof value === 'string' && /^\d{4}\/\d{1,2}\/\d{1,2}$/.test(value)) {
        const parsed = moment(value, 'YYYY/M/D', true)
        return parsed.isValid() ? parsed : null
    }

    const parsed = moment(value)
    return parsed.isValid() ? parsed : null
}

/**
 * @param {*} value
 * @param {Object|boolean} [options]
 * @returns {string}
 */
export function formatDisplayDate(value, options = {}) {
    const normalizedOptions = typeof options === 'boolean'
        ? { includeTime: options }
        : options

    const {
        dateFormat = DEFAULT_DATE_FORMAT,
        includeTime = false,
        includeWeekday = false,
        style = 'short',
        locale,
        emptyValue = '-',
        hourSuffix = false
    } = normalizedOptions

    const parsed = parseDisplayDateValue(value)
    if (!parsed) {
        return emptyValue
    }

    const patterns = dateDisplayPatterns(locale != null ? locale : getLocale(), dateFormat)
    const resolvedLocale = patterns.locale
    const twelveHour = patterns.time === 'h:mm A'
    if (style === 'long' || style === 'monthYear') {
        // Format wall-clock fields, including UTC-naive location dates, without shifting them.
        const calendarDate = new Date(0)
        calendarDate.setUTCFullYear(parsed.year(), parsed.month(), parsed.date())
        calendarDate.setUTCHours(12, 0, 0, 0)
        const date = formatCalendarDate(calendarDate, resolvedLocale, style)
        return date + (includeTime ? ' ' + formatDisplayTime(parsed, { dateFormat, locale: resolvedLocale }) : '')
    }
    if (style === 'chart' || style === 'contextual') {
        const date = parsed.clone().locale(resolvedLocale).format(patterns.chart)
        return date + (includeTime ? ' ' + formatDisplayTime(parsed, { dateFormat, locale: resolvedLocale }) : '')
    }
    const formatted = parsed.clone().locale(resolvedLocale).format(
        includeTime ? patterns.datetime : patterns.date
    )

    if (!includeWeekday) {
        const normalized = twelveHour && includeTime
            ? normalizeUsDateTimeMeridiem(formatted)
            : formatted

        return twelveHour ? normalized : applyHourSuffix(normalized, dateFormat, includeTime && hourSuffix)
    }

    const result = `${formatWeekdayLabel(parsed, resolvedLocale)} ${formatted}`
    const normalized = twelveHour && includeTime
        ? normalizeUsDateTimeMeridiem(result)
        : result

    return twelveHour ? normalized : applyHourSuffix(normalized, dateFormat, includeTime && hourSuffix)
}

/**
 * @param {*} value
 * @param {Object|boolean} [options]
 * @returns {string}
 */
export function formatDisplayDateFromStore(value, options = {}) {
    const config = getConfig() || {}
    const dateFormat = config.dateFormat || DEFAULT_DATE_FORMAT
    const locale = getLocale()

    if (typeof options === 'boolean') {
        return formatDisplayDate(value, { dateFormat, locale, includeTime: options })
    }

    return formatDisplayDate(value, {
        dateFormat,
        locale,
        ...options
    })
}

/**
 * @param {*} value
 * @param {Object} [options]
 * @returns {string}
 */
export function formatDisplayTime(value, options = {}) {
    const {
        dateFormat = DEFAULT_DATE_FORMAT,
        locale,
        emptyValue = '',
        hourSuffix = false
    } = options

    const parsed = parseDisplayDateValue(value)
    if (!parsed) {
        return emptyValue
    }

    const patterns = dateDisplayPatterns(locale != null ? locale : getLocale(), dateFormat)
    const resolvedLocale = patterns.locale
    const formatted = parsed.clone().locale(resolvedLocale).format(
        patterns.time
    )

    const twelveHour = patterns.time === 'h:mm A'
    const normalized = twelveHour ? normalizeUsTimeMeridiem(formatted) : formatted

    return twelveHour ? normalized : applyHourSuffix(normalized, dateFormat, hourSuffix)
}

/**
 * @param {*} value
 * @param {Object} [options]
 * @returns {string}
 */
export function formatDisplayTimeFromStore(value, options = {}) {
    const config = getConfig() || {}
    const dateFormat = config.dateFormat || DEFAULT_DATE_FORMAT
    const locale = getLocale()

    return formatDisplayTime(value, {
        dateFormat,
        locale,
        ...options
    })
}

/**
 * @param {*} value
 * @param {Object} [options]
 * @returns {string}
 */
export function formatDisplayDateTimeFromStore(value, options = {}) {
    return formatDisplayDateFromStore(value, {
        includeTime: true,
        includeWeekday: false,
        ...options
    })
}

/**
 * @param {*} value
 * @param {Object} [options]
 * @returns {string}
 */
export function formatDisplayDateOnlyFromStore(value, options = {}) {
    return formatDisplayDateFromStore(value, {
        includeTime: false,
        includeWeekday: false,
        ...options
    })
}

/** Format visible label text; raw label params keep their API/storage format. */
export function formatPrintedDateFromStore(value, pattern) {
    if (typeof value === 'string') {
        // Stored APP label dates keep their EU order, independently of display region.
        const datePattern = /\b(?:\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?|\d{2}\/\d{2}\/\d{4}(?:\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AP]M)?)?)\b/gi
        if (datePattern.test(value)) {
            datePattern.lastIndex = 0
            return value.replace(datePattern, source => {
                const parsed = moment(source, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY', 'MM/DD/YYYY hh:mm A'], true)
                return parsed.isValid() ? formatPrintedDateFromStore(parsed, pattern) : source
            })
        }
        const time = moment(value, ['HH:mm:ss', 'HH:mm', 'hh:mm A'], true)
        return time.isValid() ? formatPrintedDateFromStore(time, pattern) : value
    }
    const tokens = String(pattern || '').replace(/\[[^\]]*\]/g, '')
    if (/[DMY]/.test(tokens)) {
        return formatDisplayDateFromStore(value, { includeTime: /[Hhms]/.test(tokens), emptyValue: '' })
    }
    if (/d/.test(tokens)) return formatDisplayWeekdayFromStore(value, tokens === 'dddd' ? 'long' : 'short')
    return formatDisplayTimeFromStore(value)
}

/**
 * Parsea timestamps de alarmas de timers como reloj mural del local (UTC naive).
 * @param {*} value
 * @returns {import('moment').Moment|null}
 */
function parseTimerAlarmValue(value) {
    if (value == null || value === '') {
        return null
    }

    if (moment.isMoment(value)) {
        return value.isValid() ? value.clone().utc() : null
    }

    if (typeof value === 'number') {
        const ms = value < 1e12 ? value * 1000 : value
        const parsed = moment.utc(ms)
        return parsed.isValid() ? parsed : null
    }

    const parsed = moment.utc(value)
    return parsed.isValid() ? parsed : null
}

/**
 * Reloj mural del local para comparar "hoy vs otro día" en alarmas de timers.
 * @param {*} value
 * @returns {import('moment').Moment}
 */
function resolveTimerAlarmNow(value) {
    if (value != null) {
        if (moment.isMoment(value)) {
            return value.isValid() ? value.clone().utc() : moment.utc()
        }

        if (typeof value === 'number') {
            const ms = value < 1e12 ? value * 1000 : value
            return moment.utc(ms)
        }
    }

    return moment.utc(getLocationNow())
}

/**
 * Formato para alarmas de timers: solo hora si es hoy, fecha+hora si es otro día.
 * Usa reloj mural del local, no la zona horaria del dispositivo.
 * @param {*} value
 * @param {Object} [options]
 * @param {*} [options.now] - instante de referencia del local (timestamp o moment UTC naive)
 * @returns {string}
 */
export function formatTimerAlarmFromStore(value, options = {}) {
    const { hourSuffix = true, ...restOptions } = options
    const parsed = parseTimerAlarmValue(value)
    if (!parsed) {
        return restOptions.emptyValue != null ? restOptions.emptyValue : ''
    }

    const alarm = parsed.clone()
    const now = resolveTimerAlarmNow(restOptions.now)
    const formatterOptions = { ...restOptions, hourSuffix }

    if (alarm.isSame(now, 'day')) {
        return formatDisplayTimeFromStore(alarm, formatterOptions)
    }

    return formatDisplayDateTimeFromStore(alarm, formatterOptions)
}
